import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { AvatarModule } from 'ngx-avatars';

// ALL TIME REQUIRED
import { AdminLayoutComponent } from './components/layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from './components/layouts/auth-layout/auth-layout.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';

// DIRECTIVES
import { FontSizeDirective } from './directives/font-size.directive';
import { ScrollToDirective } from './directives/scroll-to.directive';
import { AppDropdownDirective } from './directives/dropdown.directive';
import { DropdownAnchorDirective } from './directives/dropdown-anchor.directive';
import { DropdownLinkDirective } from './directives/dropdown-link.directive';
import { AccessByRoleDirective } from './directives/access-by-role.directive';

// PIPES
import { ExcerptPipe } from './pipes/excerpt.pipe';

// SERVICES
import { UploadService } from 'app/shared/services/upload.service';
import { RandomService } from 'app/shared/services/random.service';
import { BotService } from 'app/views/bots/bot.service';
import { UsersService } from '../views/users/users.service';

// COMPONENTS
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { NgxMatDatetimePickerModule } from '@angular-material-components/datetime-picker';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActiveFeatureDirective } from './directives/active-feature.directive';
import { AccessByClaimDirective } from './directives/access-by-claim.directive';
import { FileUploadComponent } from 'app/shared/components/file-upload/file-upload.component';
import { PowerbiReportComponent } from 'app/shared/components/powerbi-report/powerbi-report.component';
import { MaterialModule } from 'app/shared/material.module';
import { WebchatComponent } from 'app/views/dialogues/blocks/webchat/webchat.component';
import { EboInspectorChatCardComponent } from 'app/views/chat/chat-cards/ebo-inspector/ebo-inspector.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { GenericDatetimePickerComponent } from './components/generic-datetime-picker/generic-datetime-picker';
import { CustomPickerComponent } from './components/generic-datetime-picker/custom-picker/custom-picker';
import { TopHeaderComponent } from './components/top-header/top-header.component';
import { BackButtonComponent } from './components/back-button/back-button.component';
import { EboInputComponent } from './components/ebo-input/ebo-input.component';
import { EboAccordionComponent } from './components/ebo-accordion/ebo-accordion.component';
import { EboCardComponent } from './components/ebo-card/ebo-card.component';
import { EboToggleComponent } from './components/ebo-toggle/ebo-toggle.component';
import { EboSectionHeaderComponent } from './components/ebo-section-header/ebo-section-header.component';
import { EboTextareaComponent } from './components/ebo-textarea/ebo-textarea.component';
import { EboDropdownComponent } from './components/ebo-dropdown/ebo-dropdown.component';
import { EboWysiwygComponent } from './components/ebo-wysiwyg/ebo-wysiwyg.component';

const classesToInclude = [
  SidenavComponent,
  NotificationsComponent,
  WebchatComponent,
  AdminLayoutComponent,
  AuthLayoutComponent,
  BreadcrumbComponent,
  FontSizeDirective,
  ScrollToDirective,
  AppDropdownDirective,
  DropdownAnchorDirective,
  DropdownLinkDirective,
  AccessByRoleDirective,
  AccessByClaimDirective,
  ExcerptPipe,
  EboInspectorChatCardComponent,
  FileUploadComponent,
  PowerbiReportComponent,
  ActiveFeatureDirective,
  ResetPasswordComponent,
  GenericDatetimePickerComponent,
  CustomPickerComponent,
  TopHeaderComponent,
  BackButtonComponent,
  EboDropdownComponent,
  EboInputComponent,
  EboAccordionComponent,
  EboCardComponent,
  EboToggleComponent,
  EboSectionHeaderComponent,
  EboTextareaComponent,
  EboWysiwygComponent
];

@NgModule({
  imports: [
    AvatarModule,
    CommonModule,
    FormsModule,
    RouterModule,
    HttpClientModule,
    MaterialModule,
    PerfectScrollbarModule,
    ReactiveFormsModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule
  ],
  declarations: classesToInclude,
  exports: classesToInclude
})
export class SharedModule {
  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIcon('network_on', sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/network_on.svg'));
    iconRegistry.addSvgIcon('network_off', sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/network_off.svg'));
    iconRegistry.addSvgIcon('settings', sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/settings.svg'));
    iconRegistry.addSvgIcon('notifications', sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/notifications.svg'));
    iconRegistry.addSvgIcon(
      'notifications_active',
      sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/notifications_active.svg')
    );
    iconRegistry.addSvgIcon('dots', sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/dots.svg'));
    iconRegistry.addSvgIcon('env_production', sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/env-production.svg'));
    iconRegistry.addSvgIcon('env_test', sanitizer.bypassSecurityTrustResourceUrl('assets/styles/new-theme/svg/env-test.svg'));
  }

  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [BotService, RandomService, UploadService, UsersService]
    };
  }
}
